import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

/**
 * Overlay de "Sistema atualizando" — detecta backend offline (durante deploys/restarts).
 *
 * Tunagem conservadora (evita aparecer em qualquer blip):
 * - Carencia inicial: 20s apos o mount (nada de overlay nos primeiros segundos)
 * - Polling a cada 10s
 * - Timeout de 8s por ping
 * - Precisa de 4 falhas SEGUIDAS antes de mostrar (~40s de offline real)
 * - Quando volta online: esconde suavemente (NAO recarrega a pagina)
 */
const GRACE_MS = 20_000;
const INTERVAL_MS = 10_000;
const TIMEOUT_MS = 8_000;
const REQUIRED_FAILS = 4;

export default function MaintenanceOverlay() {
  const [offline, setOffline] = useState(false);
  const consecutiveFailsRef = useRef(0);
  const mountedAtRef = useRef(Date.now());

  const ping = useCallback(async () => {
    if (!API_URL) return;
    try {
      await axios.get(`${API_URL}/pagamentos/public-key`, {
        timeout: TIMEOUT_MS,
        validateStatus: () => true,
      });
      consecutiveFailsRef.current = 0;
      if (offline) setOffline(false);
    } catch {
      consecutiveFailsRef.current += 1;
      // Periodo de carencia: nao mostra overlay nos primeiros 20s
      if (Date.now() - mountedAtRef.current < GRACE_MS) return;
      if (consecutiveFailsRef.current >= REQUIRED_FAILS && !offline) {
        setOffline(true);
      }
    }
  }, [offline]);

  useEffect(() => {
    ping();
    const iv = setInterval(ping, INTERVAL_MS);
    return () => clearInterval(iv);
  }, [ping]);

  if (!offline) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(15, 23, 42, 0.78)', backdropFilter: 'blur(6px)' }}
    >
      <div
        className="w-full max-w-md p-8 text-center"
        style={{
          background: '#FFFFFF',
          borderRadius: '24px',
          border: '1px solid #E2E7F1',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.25)',
        }}
      >
        {/* Spinner com circulo verde */}
        <div className="flex justify-center mb-5">
          <div className="relative w-20 h-20">
            <div
              className="absolute inset-0 rounded-full"
              style={{ background: 'rgba(37, 99, 235, 0.15)' }}
            />
            <svg
              className="absolute inset-0 w-full h-full animate-spin"
              style={{ color: '#3B82F6', animationDuration: '1.4s' }}
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
              <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <svg
              className="absolute inset-0 w-full h-full p-5"
              fill="none"
              stroke="#3B82F6"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
        </div>

        <h2 className="text-xl font-bold text-gray-900 tracking-tight">
          Atualizando o sistema
        </h2>
        <p className="text-sm text-gray-500 mt-2 leading-relaxed">
          Estamos aplicando melhorias agora. Isso leva alguns segundos.
          <br />A página vai voltar sozinha quando terminar.
        </p>

        {/* Pontinhos animados */}
        <div className="flex justify-center gap-1.5 mt-6">
          {[0, 1, 2].map(i => (
            <span
              key={i}
              className="w-2 h-2 rounded-full"
              style={{
                background: '#3B82F6',
                animation: `maintenance-bounce 1.2s ease-in-out ${i * 0.15}s infinite`,
              }}
            />
          ))}
        </div>

        <p className="text-xs text-gray-400 mt-5">
          Não feche essa aba — o sistema retorna automaticamente.
        </p>
      </div>

      {/* Keyframes inline pra nao precisar mexer em CSS global */}
      <style>{`
        @keyframes maintenance-bounce {
          0%, 100% { transform: translateY(0); opacity: 0.4; }
          50% { transform: translateY(-6px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
